/**
 * Admin — Learners & Live: who's in a dialogue right now, plus per-subject
 * enrolment management (enrol / unenrol) with status.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "../../api/client";
import type { AdminLearnerRow, InProgressRow, CurriculumSummary } from "../../types";

const STATUS_LABEL: Record<string, string> = {
  not_enrolled: "Not enrolled",
  enrolled: "Enrolled",
  in_progress: "In progress",
  completed: "Completed",
};

export function AdminLearnersPage() {
  const qc = useQueryClient();
  const [selCurr, setSelCurr] = useState<Record<string, string>>({});

  const live = useQuery({
    queryKey: ["admin-in-progress"],
    queryFn: () =>
      apiGet<{ in_progress: InProgressRow[]; active_window_minutes: number; abandoned_count: number }>(
        "/api/admin/in-progress"
      ),
    refetchInterval: 15000, // refresh the live panel every 15s
  });
  const learners = useQuery({
    queryKey: ["admin-learners"],
    queryFn: () => apiGet<{ learners: AdminLearnerRow[] }>("/api/admin/learners"),
  });
  const curricula = useQuery({
    queryKey: ["curricula"],
    queryFn: () => apiGet<CurriculumSummary[]>("/api/curricula"),
  });

  const enroll = useMutation({
    mutationFn: ({ id, curriculum_id }: { id: string; curriculum_id: string }) =>
      apiPost(`/api/admin/learners/${id}/enroll`, { curriculum_id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-learners"] }),
  });
  // Unenrolling drops the learner's progress in that subject, so it asks first.
  // Keyed "<learner id>:<curriculum id>" — only one row is ever confirming.
  const [confirming, setConfirming] = useState<string | null>(null);

  const unenroll = useMutation({
    mutationFn: ({ id, curriculum_id }: { id: string; curriculum_id: string }) =>
      apiPost(`/api/admin/learners/${id}/unenroll`, { curriculum_id }),
    onSuccess: () => {
      setConfirming(null);
      qc.invalidateQueries({ queryKey: ["admin-learners"] });
    },
  });

  return (
    <div>
      <h1>Learners</h1>
      {/* Learners add subjects themselves from Add a Subject. This page is the
          override for when that has gone wrong — the wrong subject ticked at
          sign-up, or someone who cannot work it out over the phone. */}
      <p className="text-muted">
        Who is working right now, and every learner's subjects. Learners enrol
        themselves; enrol or drop here only to correct someone's list for them.
        Mastery outcomes are on the tutor <strong>Learners</strong> tab.
      </p>

      {/* Live in-progress panel */}
      <div className="card">
        <h3>🟢 Active dialogues now</h3>
        {live.isLoading ? (
          <p className="text-muted">Loading…</p>
        ) : !live.data || live.data.in_progress.length === 0 ? (
          <p className="text-muted">No dialogues active right now.</p>
        ) : (
          <table>
            <thead><tr><th>Learner</th><th>Lesson</th><th>Progress</th><th>Elapsed</th><th>Idle</th></tr></thead>
            <tbody>
              {live.data.in_progress.map((r) => (
                <tr key={r.session_id}>
                  <td>{r.learner}</td>
                  <td>{r.lesson_title ?? r.lesson_id}</td>
                  <td>Q {r.answered} / {r.total_questions}</td>
                  <td>{r.elapsed_minutes != null ? `${r.elapsed_minutes} min` : "—"}</td>
                  <td>{r.idle_minutes != null ? `${r.idle_minutes} min` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="text-muted" style={{ fontSize: "0.78rem", marginTop: 8 }}>
          Shows dialogues active in the last {live.data?.active_window_minutes ?? 30} min · auto-refreshes every 15s.
          {live.data && live.data.abandoned_count > 0 && (
            <> · <strong>{live.data.abandoned_count}</strong> older unfinished dialogue{live.data.abandoned_count === 1 ? "" : "s"} (started then left) not shown.</>
          )}
        </p>
      </div>

      {/* Enrolment management */}
      <div className="card">
        <h3>📋 Enrolment</h3>
        {learners.isLoading ? (
          <p className="text-muted">Loading…</p>
        ) : (
          <table>
            <thead>
              <tr><th>Learner</th><th>Subjects &amp; status</th><th>Enrol in subject</th></tr>
            </thead>
            <tbody>
              {learners.data?.learners.map((l) => (
                <tr key={l.id}>
                  <td>
                    <strong>{l.name}</strong>
                    {/* The identifier someone quotes when they phone about an
                        account, so it sits with the name rather than in a
                        column of its own. */}
                    {l.student_number && (
                      <div className="student-number" style={{ fontSize: "0.75rem" }}>
                        {l.student_number}
                      </div>
                    )}
                    {l.email && <div className="text-muted" style={{ fontSize: "0.78rem" }}>{l.email}</div>}
                  </td>
                  <td>
                    {l.enrollments.length === 0 ? (
                      <span className="text-muted">— none —</span>
                    ) : (
                      l.enrollments.map((e) => (
                        <div key={e.curriculum_id} style={{ marginBottom: 4 }}>
                          <span className={`badge badge-${e.status}`}>{STATUS_LABEL[e.status]}</span>{" "}
                          {e.title} <span className="text-muted">({e.lessons_completed}/{e.total_lessons})</span>{" "}
                          {confirming === `${l.id}:${e.curriculum_id}` ? (
                            <>
                              <span className="text-muted">Drop this subject and its progress?</span>{" "}
                              <button
                                className="btn btn-sm btn-primary"
                                disabled={unenroll.isPending}
                                onClick={() => unenroll.mutate({ id: l.id, curriculum_id: e.curriculum_id })}
                              >
                                {unenroll.isPending ? "Unenrolling…" : "Yes, unenrol"}
                              </button>{" "}
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => setConfirming(null)}
                              >
                                Keep
                              </button>
                            </>
                          ) : (
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => setConfirming(`${l.id}:${e.curriculum_id}`)}
                            >
                              Unenrol
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </td>
                  <td>
                    <select
                      value={selCurr[l.id] ?? ""}
                      onChange={(ev) => setSelCurr((s) => ({ ...s, [l.id]: ev.target.value }))}
                    >
                      <option value="">Choose subject…</option>
                      {curricula.data?.map((c) => (
                        <option key={c.id} value={c.id}>{c.title}</option>
                      ))}
                    </select>{" "}
                    <button
                      className="btn btn-sm btn-primary"
                      disabled={!selCurr[l.id] || enroll.isPending}
                      onClick={() => enroll.mutate({ id: l.id, curriculum_id: selCurr[l.id] })}
                    >
                      Enrol
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
